import { createContext, useContext, useState, useEffect, ReactNode } from "react";
import { useAuth } from "@/hooks/useAuth";
import { TOUR_STORAGE_KEYS } from "./tourConfig";

interface BrokerTourContextType {
  isTourRunning: boolean;
  startTour: () => void;
  stopTour: () => void;
  completeTour: () => void;
  skipTour: () => void;
  isTourCompleted: boolean;
  isTourDismissed: boolean;
  shouldShowWelcome: boolean;
  dismissWelcome: () => void;
}

const BrokerTourContext = createContext<BrokerTourContextType | undefined>(undefined);

export function useBrokerTour() {
  const context = useContext(BrokerTourContext);
  if (!context) {
    throw new Error("useBrokerTour must be used within BrokerTourProvider");
  }
  return context;
}

interface BrokerTourProviderProps {
  children: ReactNode;
}

export function BrokerTourProvider({ children }: BrokerTourProviderProps) {
  const { user } = useAuth();
  const [isTourRunning, setIsTourRunning] = useState(false);
  const [isTourCompleted, setIsTourCompleted] = useState(false);
  const [isTourDismissed, setIsTourDismissed] = useState(false);

  // Load tour completion status from localStorage
  useEffect(() => {
    if (!user?.id) return;

    const completedKey = TOUR_STORAGE_KEYS.completed(user.id);
    const dismissedKey = TOUR_STORAGE_KEYS.dismissed(user.id);

    const completed = localStorage.getItem(completedKey) === "true";
    const dismissed = localStorage.getItem(dismissedKey) === "true";

    setIsTourCompleted(completed);
    setIsTourDismissed(dismissed);
  }, [user?.id]);

  const startTour = () => {
    setIsTourRunning(true);
  };

  const stopTour = () => {
    setIsTourRunning(false);
  };

  const completeTour = () => {
    if (!user?.id) return;

    const completedKey = TOUR_STORAGE_KEYS.completed(user.id);
    localStorage.setItem(completedKey, "true");
    setIsTourCompleted(true);
    setIsTourRunning(false);
  };

  const skipTour = () => {
    if (!user?.id) return;

    const dismissedKey = TOUR_STORAGE_KEYS.dismissed(user.id);
    localStorage.setItem(dismissedKey, "true");
    setIsTourDismissed(true);
    setIsTourRunning(false);
  };

  const dismissWelcome = () => {
    if (!user?.id) return;

    const dismissedKey = TOUR_STORAGE_KEYS.dismissed(user.id);
    localStorage.setItem(dismissedKey, "true");
    setIsTourDismissed(true);
  };

  // Determine if we should show the welcome message
  const shouldShowWelcome = !isTourCompleted && !isTourDismissed;

  const value: BrokerTourContextType = {
    isTourRunning,
    startTour,
    stopTour,
    completeTour,
    skipTour,
    isTourCompleted,
    isTourDismissed,
    shouldShowWelcome,
    dismissWelcome,
  };

  return (
    <BrokerTourContext.Provider value={value}>
      {children}
    </BrokerTourContext.Provider>
  );
}

